// ─── Auth Store (Zustand) ─────────────────────────────────────────────────────
// Story 3: Secure Auth State Management
// - Access token stored in memory ONLY (never localStorage/sessionStorage)
// - Refresh token is HTTP-only cookie (server-managed)
// - Multi-tab logout via BroadcastChannel
// - Race-condition-safe refresh (handled in api.ts interceptor queue)

import { create } from 'zustand';
import { configureApiAuth } from '../services/api';
import { AuthState, User, UserRole } from '../types/auth.types';

// ─── Store Interface ──────────────────────────────────────────────────────────

interface AuthStore extends AuthState {
  // Actions
  setUser: (user: User, accessToken: string) => void;
  setAccessToken: (token: string) => void;
  clearAuth: () => void;
  setStatus: (status: AuthState['status']) => void;
  // Getters
  hasRole: (role: UserRole) => boolean;
}

// ─── Initial State ────────────────────────────────────────────────────────────

const initialState: AuthState = {
  user: null,
  accessToken: null,
  status: 'idle',
  isAuthenticated: false,
  isAdmin: false,
  isStaff: false,
};

// ─── Multi-Tab Logout via BroadcastChannel ────────────────────────────────────

const LOGOUT_CHANNEL = 'onetech_auth_logout';
let broadcastChannel: BroadcastChannel | null = null;

if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
  broadcastChannel = new BroadcastChannel(LOGOUT_CHANNEL);
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useAuthStore = create<AuthStore>((set, get) => {
  // Wire up the Axios interceptor token accessors
  configureApiAuth({
    getToken: () => get().accessToken,
    onSuccess: (token: string) => get().setAccessToken(token),
    onFailure: () => get().clearAuth(),
  });

  // Listen for logout from other tabs
  broadcastChannel?.addEventListener('message', (event) => {
    if (event.data === 'logout') {
      set({ ...initialState, status: 'unauthenticated' });
    }
  });

  return {
    ...initialState,

    setUser: (user: User, accessToken: string) => {
      set({
        user,
        accessToken,
        status: 'authenticated',
        isAuthenticated: true,
        isAdmin: user.role === 'admin' || user.role === 'superadmin',
        isStaff: user.role === 'staff' || user.role === 'admin' || user.role === 'superadmin',
      });
    },

    setAccessToken: (token: string) => {
      set({ accessToken: token });
    },

    clearAuth: () => {
      set({ ...initialState, status: 'unauthenticated' });
      // Notify other tabs to also log out
      broadcastChannel?.postMessage('logout');
    },

    setStatus: (status: AuthState['status']) => {
      set({ status });
    },

    hasRole: (role: UserRole) => {
      const { user } = get();
      if (!user) return false;

      const roleHierarchy: Record<UserRole, number> = {
        customer: 0,
        staff: 1,
        admin: 2,
        superadmin: 3,
      };

      return roleHierarchy[user.role] >= roleHierarchy[role];
    },
  };
});

// ─── Selector Hooks ───────────────────────────────────────────────────────────

export const useCurrentUser = () => useAuthStore((s) => s.user);
export const useIsAuthenticated = () => useAuthStore((s) => s.isAuthenticated);
export const useIsAdmin = () => useAuthStore((s) => s.isAdmin);
export const useAuthStatus = () => useAuthStore((s) => s.status);
export const useAccessToken = () => useAuthStore((s) => s.accessToken);